/* eslint-disable max-lines */
import React, {
    useContext, useEffect, useState
} from 'react';
import {
    useParams, useNavigate, useLocation, Link
} from 'react-router-dom';
import {
    Palette, Type, Maximize2, Code, Copy, Check, CalendarDays, User, Zap, MessageSquare, Mail, Globe, Phone, Trash2, ChevronLeft, Wifi, WifiOff, RefreshCw, PhoneOff, Database, X
} from 'lucide-react';
import EntityJsonView from 'builders/destudio/components/EntityJsonView';
import {
    Card, CardContent, CardHeader, CardTitle
} from 'shared/components/ui/card';
import {
    Button
} from 'shared/components/ui/button';
import {
    Separator
} from 'shared/components/ui/separator';
import {
    OrganizationContext
} from 'shared/context/OrganizationContext';
import {
    Context
} from 'shared/context/GlobalState';
import {
    getOrganizationMember
} from 'shared/utils/organizationUtils';
import {
    NestedPageLayout
} from 'shared/components/NewDesignComponents/PageLayout';
import Breadcrumb from 'shared/components/Breadcrumb';
import {
    channelService, opcodeAssistantService, baileysBridgeService, redisCollectionService
} from 'shared/api';
import {
    Input
} from 'shared/components/ui/input';
import withLoader from 'shared/hooks/withLoader';
import UpdateWebChannelWrapper, {
    WebChannelUpdateDialog
} from '../WebChatUpdate';
import WhatsAppUpdate from '../WhatsApp/WhatsAppUpdate';
import BaileysConnectionPanel from '../WhatsApp/BaileysConnectionPanel';
import MessengerUpdate from '../Messenger/MessengerUpdate';
import VoiceUpdate from '../Voice/VoiceUpdate';
import {
    ChannelTypeUpdateDialog
} from '../ChannelType';
import type {
    Channel, WebChatConfigType, LoaderProps
} from '../../types/builderChannel.types';
import {
    Tabs, TabsContent, TabsList, TabsTrigger
} from 'shared/components/ui/tabs';
import {
    AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
    AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from 'shared/components/ui/alert-dialog';
import {
    ConversationHistoryTab
} from './ConversationHistoryTab';
import {
    PanelErrorBoundary
} from 'shared/components/ErrorBoundary';
import {
    FaWhatsapp, FaFacebookMessenger
} from 'react-icons/fa';
import { cn } from 'shared/lib/utils';
import PhoneInput from 'react-phone-input-2';

const CHANNEL_META: Record<string, { icon: React.ReactNode; bg: string; color: string }> = {
    Email: { icon: <Mail size={22} className="text-white" />, bg: 'bg-blue-500', color: '#3b82f6' },
    Webchat: { icon: <Globe size={22} className="text-white" />, bg: 'bg-emerald-500', color: '#10b981' },
    'Web Chat': { icon: <Globe size={22} className="text-white" />, bg: 'bg-emerald-500', color: '#10b981' },
    WhatsApp: { icon: <FaWhatsapp size={22} className="text-white" />, bg: 'bg-[#25D366]', color: '#25D366' },
    Whatsapp: { icon: <FaWhatsapp size={22} className="text-white" />, bg: 'bg-[#25D366]', color: '#25D366' },
    Messenger: { icon: <FaFacebookMessenger size={22} className="text-white" />, bg: 'bg-[#0084FF]', color: '#0084FF' },
    Voice: { icon: <Phone size={22} className="text-white" />, bg: 'bg-amber-500', color: '#f59e0b' },
    // Lowercase keys (S-0005: server stores lowercase `type`)
    email: { icon: <Mail size={22} className="text-white" />, bg: 'bg-blue-500', color: '#3b82f6' },
    webchat: { icon: <Globe size={22} className="text-white" />, bg: 'bg-emerald-500', color: '#10b981' },
    whatsapp: { icon: <FaWhatsapp size={22} className="text-white" />, bg: 'bg-[#25D366]', color: '#25D366' },
    messenger: { icon: <FaFacebookMessenger size={22} className="text-white" />, bg: 'bg-[#0084FF]', color: '#0084FF' },
    voice: { icon: <Phone size={22} className="text-white" />, bg: 'bg-amber-500', color: '#f59e0b' },
};

export default function ChannelDetails() {
  const {
 oragID, subOragID, channelId
} = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const isDeStudio = location.pathname.includes('/de-studio/');

  const fetchAssistant = async () => {
    const response = await channelService.getChannelById(
      oragID!,
      subOragID!,
      channelId!
    );
    return response.data as Channel;
  };

  const MyComponentWithLoader = withLoader(
    ChannelDetailsData,
    fetchAssistant
  );

  return (
      <NestedPageLayout className={''}>
          <div className="my-3">
              {isDeStudio ? (
                  <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => navigate(`/organizations/${oragID}/sub/${subOragID}/de-studio/library?tab=channels`)}
                      className="h-8 px-2 text-muted-foreground hover:text-primary transition-colors flex items-center gap-1.5 -ml-2"
                  >
                      <ChevronLeft className="h-4 w-4" />
                      <span className="text-xs font-medium">Back to Library</span>
                  </Button>
              ) : (
                  <Breadcrumb maxPaths={2} />
              )}
          </div>
          {/* Tier 3 — the loaded channel document renders below. The
              breadcrumb / back button above stays mounted, so a malformed
              channel record leaves the user a way out instead of a blank page. */}
          <PanelErrorBoundary
              name="channel-details"
              title="This channel could not be displayed"
              recordType="channel"
              recordId={channelId}
              resetKeys={[channelId]}
          >
              <MyComponentWithLoader />
          </PanelErrorBoundary>
      </NestedPageLayout>
  );
}

const ChannelDetailsData: React.FC<LoaderProps> = ({
  data
}) => {
  const noop = () => {};
  const [emailUpdateDialog, setEmailUpdateDialog] = useState(false);
  const [channelDetails, setChannelDetails] = useState<Channel>(data);
  const [createdByName, setCreatedByName] = useState('-');
  const [assistantName, setAssistantName] = useState('-');
  const [collectionName, setCollectionName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(false);
  const {
      oragID, subOragID
  } = useParams();
  const navigate = useNavigate();

  const {
  selectedOrganization
} = useContext(OrganizationContext);

  const [whatsAppUpdateDialog, setWhatsAppUpdateDialog] = useState(false);
  const [webChatUpdateDialog, setWebChatUpdateDialog] = useState(false);
  const [messengerUpdateDialog, setMessengerUpdateDialog] = useState(false);
  const [voiceUpdateDialog, setVoiceUpdateDialog] = useState(false);
  const [showJson, setShowJson] = useState(false);

  const typeKey = (channelDetails.type || '').toLowerCase();
  const meta = CHANNEL_META[channelDetails.type] ?? CHANNEL_META[typeKey] ?? { icon: <MessageSquare size={22} className="text-white" />, bg: 'bg-slate-500', color: '#6b7280' };
  const isWhatsApp = typeKey === 'whatsapp';
  // `provider` is the authority — redisChannelSchema requires it on create, so every
  // channel written since S-0005 carries one. Reading the ADDRESS instead was wrong two
  // ways: a channel created over the API/MCP (whose Baileys address is a bare session
  // UUID, and whose Twilio address need not be `whatsapp:+…`) was mis-typed, and every
  // `coexistence` channel read as Baileys. The address heuristic survives only as the
  // fallback for pre-schema records that genuinely have no provider.
  const providerKey = (channelDetails.provider || '').toLowerCase();
  const isBaileys = isWhatsApp && (providerKey
    ? providerKey === 'baileys'
    : !!channelDetails.address && !channelDetails.address.startsWith('whatsapp:+'));
  // A listener harvests inbound messages into a KB collection and never replies. The
  // Bridge needs to be told this at link time (mode=listener); see the connect panel.
  const isListenerChannel = (channelDetails.send_mode ?? 'auto') === 'disabled'
    && (channelDetails.receive_enabled ?? true);

  useEffect(() => {
    getOrganizationMember(channelDetails.createdBy, selectedOrganization)
      .then((name) => { setCreatedByName(name); })
      .catch(() => {});
  }, [channelDetails.createdBy, selectedOrganization]);

  useEffect(() => {
    if (channelDetails.assistant_id && oragID && subOragID) {
        opcodeAssistantService.getOpcodeAssistant(oragID, subOragID, channelDetails.assistant_id)
            .then((response) => { setAssistantName(response.data.title); })
            .catch(() => {});
    }
  }, [channelDetails.assistant_id, oragID, subOragID]);

  // Resolve the bound KB collection's display name (listener channels harvest into
  // it). Falls back to the raw id so the link renders even if the lookup fails.
  useEffect(() => {
    const collectionId = (channelDetails as any).collectionId;
    if (!collectionId || !oragID || !subOragID) return;
    redisCollectionService.listCollections(oragID, subOragID)
        .then((res: any) => {
            const list = (res.data ?? res) as Array<{ _id: string; name: string }>;
            const match = Array.isArray(list) ? list.find((c) => c._id === collectionId) : undefined;
            setCollectionName(match?.name || collectionId);
        })
        .catch(() => { setCollectionName(collectionId); });
  }, [(channelDetails as any).collectionId, oragID, subOragID]);

  const handleOpenUpdate = () => {
    const normalizedType = channelDetails.type.toLowerCase().trim();
    switch (normalizedType) {
      case 'email': setEmailUpdateDialog(true); break;
      case 'whatsapp': setWhatsAppUpdateDialog(true); break;
      case 'webchat': setWebChatUpdateDialog(true); break;
      case 'messenger': setMessengerUpdateDialog(true); break;
      case 'voice': setVoiceUpdateDialog(true); break;
      default: break;
    }
  };

  const handleDelete = async () => {
    try {
      await channelService.deleteChannel({
        org_id: oragID!,
        sub_org_id: subOragID!,
        channelId: channelDetails._id,
      });
      navigate(`/organizations/${oragID}/sub/${subOragID}/channels`);
    } catch (err: any) {
      alert('Delete failed: ' + (err?.response?.data?.message || err?.message || String(err)));
    }
  };

  return (
      <div className="space-y-6">
          {/* Color accent strip */}
          <div
              className="h-[3px] rounded-full -mx-1"
              style={{ background: `linear-gradient(90deg, ${meta.color} 0%, transparent 80%)` }}
          />

          {/* Page header */}
          <div className="flex items-center justify-between">
              <div className="flex items-center gap-4">
                  <div className={cn('flex items-center justify-center size-12 rounded-xl shrink-0', meta.bg)}>
                      {meta.icon}
                  </div>
                  <div>
                      <h1 className="text-xl font-bold text-foreground leading-tight">
                          {channelDetails.topic || 'Unnamed Channel'}
                      </h1>
                      <div className="flex items-center gap-2 mt-1 flex-wrap">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-medium">
                              {channelDetails.type}
                          </span>
                          {channelDetails.receive_enabled === false ? (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-amber-100 text-amber-700 dark:bg-amber-950 dark:text-amber-300 text-[11px] font-semibold">
                                  Send only
                              </span>
                          ) : (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-green-100 text-green-700 dark:bg-green-950 dark:text-green-300 text-[11px] font-semibold">
                                  Receiving
                              </span>
                          )}
                          {channelDetails.send_mode === 'auto' && (
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-blue-100 text-blue-700 dark:bg-blue-950 dark:text-blue-300 text-[11px] font-semibold">
                                  <Zap size={9} />
                                  Auto-reply
                              </span>
                          )}
                          {channelDetails.send_mode === 'tool' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-semibold">
                                  Sends when triggered
                              </span>
                          )}
                          {channelDetails.send_mode === 'disabled' && (
                              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-muted text-muted-foreground text-[11px] font-semibold">
                                  No outbound
                              </span>
                          )}
                      </div>
                  </div>
              </div>
              <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    onClick={() => setDeleteConfirm(true)}
                    className="h-9 px-3 gap-1.5 text-xs text-destructive border-destructive/40 hover:bg-destructive/5 hover:border-destructive"
                  >
                      <Trash2 className="h-3.5 w-3.5" />
                      Delete
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => setShowJson(true)}
                    className="h-9 px-3 gap-1.5 text-xs"
                  >
                      <Code className="h-3.5 w-3.5" />
                      JSON
                  </Button>
                  <Button
                    variant="default"
                    onClick={handleOpenUpdate}
                    className="h-9 px-4 text-xs"
                  >
                      Manage
                  </Button>
              </div>
          </div>

          <Tabs defaultValue="info" className="w-full">
              <TabsList className="inline-flex h-10 items-center justify-start rounded-none border-b bg-transparent p-0 w-full gap-0">
                  <TabsTrigger
                    value="info"
                    className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                      Details
                  </TabsTrigger>
                  <TabsTrigger
                    value="history"
                    className="rounded-none border-b-2 border-transparent px-4 py-2 text-sm font-medium text-muted-foreground shadow-none transition-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-foreground data-[state=active]:shadow-none"
                  >
                      Conversation History
                  </TabsTrigger>
              </TabsList>

              <TabsContent value="info" className="mt-6 space-y-4">
                  {/* Info card */}
                  <Card>
                      <CardContent className="pt-6">
                          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
                              <InfoItem
                                icon={<CalendarDays size={14} />}
                                label="Created"
                                value={channelDetails.created
                                    ? new Date(channelDetails.created).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' })
                                    : '—'}
                              />
                              <InfoItem
                                icon={<User size={14} />}
                                label="Created by"
                                value={createdByName || '-'}
                              />
                              <InfoItem
                                icon={<Code size={14} />}
                                label="Channel ID"
                                value={channelDetails._id}
                                mono
                              />
                              {channelDetails.address && (
                                  <InfoItem
                                    icon={<Globe size={14} />}
                                    label="Address"
                                    value={channelDetails.address}
                                    mono
                                  />
                              )}
                              {channelDetails.assistant_id ? (
                                  <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <MessageSquare size={14} />
                                          <span className="font-medium">Assistant</span>
                                      </div>
                                      <Link
                                        to={`/organizations/${oragID}/sub/${subOragID}/de-studio/unified-builder/${channelDetails.assistant_id}`}
                                        className="text-sm font-medium text-primary hover:underline"
                                      >
                                          {assistantName}
                                      </Link>
                                  </div>
                              ) : (
                                  <InfoItem icon={<MessageSquare size={14} />} label="Assistant" value="-" />
                              )}
                              {(channelDetails as any).collectionId ? (
                                  <div className="flex flex-col gap-1">
                                      <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                                          <Database size={14} />
                                          <span className="font-medium">Collection</span>
                                      </div>
                                      <Link
                                        to={`/organizations/${oragID}/sub/${subOragID}/knowledge-builder/${(channelDetails as any).collectionId}`}
                                        className="text-sm font-medium text-primary hover:underline"
                                      >
                                          {collectionName || (channelDetails as any).collectionId}
                                      </Link>
                                  </div>
                              ) : (
                                  channelDetails.send_mode === 'disabled' && (channelDetails.receive_enabled ?? true) && (
                                      <InfoItem icon={<Database size={14} />} label="Collection" value="-" />
                                  )
                              )}
                          </div>
                      </CardContent>
                  </Card>

                  {/* Baileys connection status. Shown for EVERY Baileys channel: the
                      WhatsApp link is what the channel sends *and* receives over, so a
                      send-only channel (receive_enabled false, send_mode "tool") needs it
                      just as much. Gating on receive_enabled hid the only place such a
                      channel could ever be linked. */}
                  {isBaileys && (
                      <BaileysConnectionStatus
                          organizationId={oragID!}
                          subOrganizationId={subOragID!}
                          address={channelDetails.address}
                          isListener={isListenerChannel}
                      />
                  )}

                  {/* WebChat config */}
                  {typeKey === 'webchat' && (
                      <div className="mt-4">
                          <WebChatConfig
                        channelDetails={channelDetails}
                        setChannelDetails={setChannelDetails}
                        config={channelDetails.webChatConfig || {}}
                      />
                      </div>
                  )}
              </TabsContent>

              <TabsContent value="history" className="mt-6">
                  <PanelErrorBoundary
                    name="channel-conversation-history"
                    title="Conversation history could not be displayed"
                    recordType="channel"
                    recordId={channelDetails.address}
                    recordLabel={channelDetails.name}
                  >
                      <ConversationHistoryTab channelAddress={channelDetails.address} />
                  </PanelErrorBoundary>
              </TabsContent>
          </Tabs>

          {whatsAppUpdateDialog && (
          <WhatsAppUpdate
            isOpen={whatsAppUpdateDialog}
            setOpenWhatsAppModal={setWhatsAppUpdateDialog}
            channel={channelDetails}
            setEmailChannels={noop}
          />
      )}
          {emailUpdateDialog && (
          <ChannelTypeUpdateDialog
          isOpen={emailUpdateDialog}
          setOpenEmailModal={setEmailUpdateDialog}
          channel={channelDetails}
          setEmailChannels={noop}
        />
      )}
          {webChatUpdateDialog && (
          <WebChannelUpdateDialog
          openEmailModal={webChatUpdateDialog}
          setOpenEmailModal={setWebChatUpdateDialog}
          emailUpdateObject={channelDetails}
          setEmailUpdateObject={(updatedChannel: Channel) =>
            setChannelDetails(updatedChannel)
          }
          setEmailChannels={noop}
        />
      )}
          {messengerUpdateDialog && (
          <MessengerUpdate
          isOpen={messengerUpdateDialog}
          setOpenMessengerModal={setMessengerUpdateDialog}
          channel={channelDetails}
          setEmailChannels={noop}
        />
      )}
          {voiceUpdateDialog && (
          <VoiceUpdate
          isOpen={voiceUpdateDialog}
          setOpenVoiceModal={setVoiceUpdateDialog}
          channel={channelDetails} setEmailChannels={noop} />
      )}
          <EntityJsonView
            open={showJson}
            onOpenChange={setShowJson}
            title="Channel JSON"
            entity={channelDetails}
            onApply={(parsed) => {
                if (!parsed || typeof parsed !== 'object') return;
                setChannelDetails(parsed as Channel);
            }}
          />

          <AlertDialog open={deleteConfirm} onOpenChange={setDeleteConfirm}>
              <AlertDialogContent>
                  <AlertDialogHeader>
                      <AlertDialogTitle>Delete this channel?</AlertDialogTitle>
                      <AlertDialogDescription>
                          <b>{channelDetails.topic || 'This channel'}</b> will be permanently removed.
                          Any active connections routed through it will stop working immediately.
                      </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                      <AlertDialogAction
                          onClick={handleDelete}
                          className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
                      >
                          Delete
                      </AlertDialogAction>
                  </AlertDialogFooter>
              </AlertDialogContent>
          </AlertDialog>
      </div>
  );
};

const InfoItem: React.FC<{ icon: React.ReactNode; label: string; value: string; mono?: boolean }> = ({
  icon, label, value, mono,
}) => (
    <div className="flex flex-col gap-1">
        <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
            {icon}
            <span className="font-medium">{label}</span>
        </div>
        <span className={cn('text-sm font-medium text-foreground break-all', mono && 'font-mono text-xs')}>
            {value}
        </span>
    </div>
);

const WebChatConfig: React.FC<{
  config: WebChatConfigType;
  channelDetails: Channel;
  setChannelDetails: React.Dispatch<React.SetStateAction<Channel>>;
}> = ({
 config, channelDetails, setChannelDetails
}) => {
  const [updateDialog, setUpdateDialog] = useState(false);
  const [dialogType, setDialogType] = useState<'appearance-launcher' | 'home-screen'>('appearance-launcher');

  const handleOpenDialog = (type: 'appearance-launcher' | 'home-screen') => {
    setUpdateDialog(true);
    setDialogType(type);
  };

  return (
      <Card className="w-full">
          <CardHeader>
              <CardTitle className="text-base">Web Chat Configuration</CardTitle>
          </CardHeader>
          <CardContent>
              <div className="space-y-6">
                  <ConfigSection
            icon={<Type className="w-4 h-4" />}
            title="Appearance & Launcher"
            content={
                <div className="flex flex-col gap-4">
                    <div className="space-y-2">
                        <ColorDisplay label="Accent" color={config.accentColor} />
                        <ColorDisplay label="Text Accent" color={config.textAccentColor} />
                    </div>
                    <div className="space-y-1.5">
                        <span className="flex items-center gap-1.5 text-xs text-muted-foreground">
                            <Maximize2 className="w-3 h-3" /> Dimensions
                        </span>
                        <span className="text-sm">{config.greetingMessageWidth} × {config.greetingMessageHeight}</span>
                    </div>
                    <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => handleOpenDialog('appearance-launcher')}>
                            Manage appearance
                        </Button>
                    </div>
                </div>
            }
          />

                  <Separator />

                  <ConfigSection
            icon={<Palette className="w-4 h-4" />}
            title="Home Screen"
            content={
                <div className="flex flex-col gap-4">
                    {config.assistantName && <span className="text-sm font-medium">{config.assistantName}</span>}
                    <div className="grid grid-cols-2 gap-4">
                        <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground">Primary</p>
                            <ColorDisplay label="Primary" color={config.primaryColor} />
                            <ColorDisplay label="Secondary" color={config.secondaryColor} />
                        </div>
                        <div className="space-y-1.5">
                            <p className="text-xs font-medium text-muted-foreground">Text</p>
                            <ColorDisplay label="Text Primary" color={config.textPrimaryColor} />
                            <ColorDisplay label="Text Secondary" color={config.textSecondaryColor} />
                        </div>
                    </div>
                    {config.greetingMessage && (
                        <div>
                            <p className="text-xs font-medium text-muted-foreground mb-1">Greeting</p>
                            <p className="text-sm">{config.greetingMessage}</p>
                        </div>
                    )}
                    <div className="flex justify-end">
                        <Button variant="outline" size="sm" onClick={() => handleOpenDialog('home-screen')}>
                            Manage home screen
                        </Button>
                    </div>
                </div>
            }
          />
              </div>

              <UpdateWebChannelWrapper
                channel={{
                  ...channelDetails,
                  assistant_id: channelDetails.assistant_id || '',
                }}
                setEmailChannel={(updatedChannel: any) => setChannelDetails(updatedChannel)}
                isOpen={updateDialog}
                setOpenEmailModal={setUpdateDialog}
                type={dialogType}
                />
          </CardContent>
      </Card>
  );
};

const ConfigSection: React.FC<{
  icon: React.ReactNode;
  title: string;
  content: React.ReactNode;
}> = ({
 icon, title, content
}) => (
    <div>
        <h3 className="flex items-center gap-2 text-sm font-semibold text-foreground mb-3">
            {icon}
            <span>{title}</span>
        </h3>
        {content}
    </div>
);

const ColorDisplay: React.FC<{ label: string; color?: string }> = ({
  label,
  color,
}) => (
    <div className="flex items-center gap-2">
        <div
      className="w-4 h-4 rounded-full border border-border shrink-0"
      style={{ backgroundColor: color }}
    />
        <span className="text-xs text-muted-foreground">
            {label}: <span className="font-mono text-foreground">{color || '—'}</span>
        </span>
    </div>
);

const CopyRow: React.FC<{ label: string; value: string }> = ({ label, value }) => {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(value).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };
  return (
      <div className="flex flex-col gap-1">
          <span className="text-xs text-muted-foreground font-medium">{label}</span>
          <div className="flex items-center gap-2 px-3 py-2 bg-muted/50 border border-border rounded-lg">
              <code className="flex-grow text-[11px] font-mono text-foreground overflow-hidden text-ellipsis whitespace-nowrap">
                  {value}
              </code>
              <button
                  type="button"
                  onClick={handleCopy}
                  className="h-6 w-6 flex items-center justify-center rounded hover:bg-accent shrink-0 transition-colors"
              >
                  {copied
                      ? <Check size={12} className="text-emerald-500" />
                      : <Copy size={12} className="text-muted-foreground" />}
              </button>
          </div>
      </div>
  );
};

// ─── BaileysConnectionStatus ──────────────────────────────────────────────────
// Live connection panel for Baileys WhatsApp channels (conversation + receive_only).
// Polls every 10s and shows connected phone, disconnected state, and reconnect options.

type BaileysStatus = 'loading' | 'connected' | 'disconnected' | 'pending' | 'pairing' | 'reconnecting' | 'reauth_required' | 'degraded';
type ReconnectMode = 'qr' | 'phone';

function formatPhone(jid: string): string {
    const stripped = jid.split('@')[0].split(':')[0];
    return stripped.startsWith('+') ? stripped : `+${stripped}`;
}

const BaileysConnectionStatus: React.FC<{
    organizationId: string;
    subOrganizationId: string;
    address: string;
    /** Listener channel — the Bridge must be told at link time, or it persists "webhook". */
    isListener?: boolean;
}> = ({
    organizationId, subOrganizationId, address, isListener = false,
}) => {
    const { addNewNotifcation } = useContext(Context);
    const [status, setStatus] = useState<BaileysStatus>('loading');
    const [phone, setPhone] = useState('');
    const [reconnecting, setReconnecting] = useState(false);
    const [reconnectMode, setReconnectMode] = useState<ReconnectMode>('qr');
    const [qrCode, setQrCode] = useState('');
    const [pairingCode, setPairingCode] = useState('');
    const [pairingPhone, setPairingPhone] = useState('');
    const [actionLoading, setActionLoading] = useState(false);
    // User forced a re-link from an otherwise-connected/reconnecting card. Reveals the
    // QR/phone panel even when the backend still reports a live-ish state.
    const [forceReauth, setForceReauth] = useState(false);
    // The user asked for a QR; until it arrives the shared BaileysConnectionPanel
    // renders its 'Generating' state (same layout as channel creation).
    const [qrRequested, setQrRequested] = useState(false);
    // Consecutive polls where the bridge reported a finished link attempt and no QR.
    // A single such poll means nothing: /qr answers immediately with 'disconnected' and
    // only reaches 'pending' once Baileys has woken up (~4s), so one sample can land in
    // that gap and would cancel the click a second after the user made it.
    const endedPolls = React.useRef(0);
    // Whether a link attempt is in flight right now, readable from effect cleanups (which
    // capture stale state, not refs). Drives both the Stop button and the teardown below.
    const attemptInFlight = React.useRef(false);
    // A remount must not adopt an attempt started before it — see the teardown effect.
    const adoptedExisting = React.useRef(false);
    // Whether the LAST status poll actually reached the bridge. A transport failure must be
    // shown as "can't reach", distinct from a real WhatsApp 'disconnected'/'reauth_required'.
    const [reachable, setReachable] = useState(true);
    // Age (ms) of the bridge's last confirmed-alive stamp, for a truthful "alive Ns ago".
    const [staleMs, setStaleMs] = useState<number | null>(null);
    // True while the bridge is inside WhatsApp's expected post-scan restart (the pairing
    // handshake, ~2–10s). Rendered as calm "finalizing" instead of the amber unstable
    // warning, and Re-authorize is hidden so users aren't coached into wiping a session
    // that is seconds away from opening (that wipe shows as "logged out" on the phone).
    const [finalizingLink, setFinalizingLink] = useState(false);
    // Epoch ms of the moment the current WhatsApp link opened (bridge lastConnectedAt) —
    // shown as 'Linked since …' so a re-authorized session has a meaningful timestamp.
    const [linkedSince, setLinkedSince] = useState<number | null>(null);

    const fetchStatus = React.useCallback(async () => {
        try {
            const res = await baileysBridgeService.checkStatus(organizationId, subOrganizationId, address);
            const s = res.data?.data?.status as BaileysStatus | undefined;
            const p = res.data?.data?.phone as string | undefined;
            const q = res.data?.data?.qr as string | undefined;
            const stale = res.data?.data?.staleMs as number | undefined;
            const fin = res.data?.data?.finalizingLink as boolean | undefined;
            setReachable(true);
            setStaleMs(typeof stale === 'number' ? stale : null);
            setFinalizingLink(fin === true);
            const lc = res.data?.data?.lastConnectedAt as number | undefined;
            setLinkedSince(typeof lc === 'number' ? lc : null);
            if (s) setStatus(s);
            if (p) setPhone(formatPhone(p));
            // Mirror the bridge's QR verbatim. This used to be gated on
            // (reconnecting || forceReauth) — both plain useState, both false again after
            // any page load or remount. So opening a channel whose session was already
            // sitting at 'pending' with a live QR fetched that QR, threw it away, and
            // rendered the "Get QR Code" button instead, forever: the panel showed
            // "Waiting for QR scan…" above an empty box and no amount of waiting or
            // reloading could fill it (observed on the Semiko channel, 2026-09-08).
            // The bridge only ever returns a `qr` when one is genuinely pending — it
            // clears it on open and on reauth_required — so its answer is the truth and
            // this component has no business second-guessing it with local intent.
            if (q) setQrCode(q);
            else if (qrCode) setQrCode('');
            // The bridge stops offering QRs once a link attempt is over — it caps how many
            // unscanned QR cycles it will mint rather than regenerating forever. When that
            // happens, drop our "the user asked for a QR" intent too, or the panel keeps
            // rendering BaileysConnectionPanel's Generating spinner over an empty QR with
            // no way back to the button short of a reload.
            if (!q && s && s !== 'pending' && s !== 'pairing') {
                endedPolls.current += 1;
                if (endedPolls.current >= 2) setQrRequested(false);
            } else {
                endedPolls.current = 0;
            }
            if (s === 'connected' && (reconnecting || forceReauth)) {
                setReconnecting(false);
                setForceReauth(false);
                setQrRequested(false);
                setPairingCode('');
                setQrCode('');
                addNewNotifcation('WhatsApp reconnected successfully!', 'success');
            }
        } catch {
            // A failed request means we couldn't REACH the bridge/proxy — it does NOT mean
            // WhatsApp logged out. Don't fabricate a 'disconnected' state (which would wrongly
            // offer a re-link); keep the last known status and flag the channel unreachable.
            // EXCEPT on first load: 'loading' renders nothing at all, so fall back to
            // 'disconnected' — a visible card with a relink option beats a vanished card.
            setReachable(false);
            setStatus((s) => (s === 'loading' ? 'disconnected' : s));
        }
    }, [organizationId, subOrganizationId, address, reconnecting, forceReauth, qrCode, addNewNotifcation]);

    useEffect(() => {
        attemptInFlight.current = qrRequested || !!qrCode || !!pairingCode;
    }, [qrRequested, qrCode, pairingCode]);

    // Stop a link attempt and return the panel to its resting state.
    const stopLinkAttempt = React.useCallback(async (notify: boolean) => {
        attemptInFlight.current = false;
        try {
            await baileysBridgeService.cancelLink(organizationId, subOrganizationId, address);
        } catch {
            // /cancel-link is newer than the server this may be talking to. Without a
            // working stop the QR simply reappears on the next poll, because the panel
            // mirrors whatever the bridge is still serving — so fall back to disconnect,
            // which every deployed server has and which reaches the same bridge session.
            //
            // Safe here precisely because of where this is called from: only ever for an
            // attempt in flight, never for a connected channel. disconnect() would log a
            // linked device out of WhatsApp; on a session that never linked there is
            // nothing to deregister, and it clears the session the same way.
            try {
                await baileysBridgeService.disconnect(organizationId, subOrganizationId, address);
            } catch {
                // Best effort. The bridge caps unscanned attempts on its own, and this
                // also fires from an unmount where there is no one left to tell.
            }
        }
        setQrRequested(false);
        setQrCode('');
        setPairingCode('');
        setPairingPhone('');
        setReconnecting(false);
        setForceReauth(false);
        endedPolls.current = 0;
        if (notify) addNewNotifcation('Link attempt stopped.', 'success');
    }, [organizationId, subOrganizationId, address, addNewNotifcation]);

    const handleStop = async () => {
        setActionLoading(true);
        try {
            await stopLinkAttempt(true);
            setStatus('disconnected');
        } finally {
            setActionLoading(false);
        }
    };

    // Initial fetch
    useEffect(() => { fetchStatus(); }, [address]);

    // A link attempt belongs to the visit that started it. Leaving the panel or reloading
    // the page ends it, rather than leaving a session on the bridge quietly minting QRs
    // that reappear the next time anyone opens this channel.
    //
    // Two halves, because neither covers the other: the cleanup handles in-app navigation
    // (a reload never runs it), and the mount check handles a reload (which lands on a
    // bridge session this panel never started). Cancelling is idempotent and the bridge
    // refuses it for a connected session, so both can fire freely.
    useEffect(() => {
        adoptedExisting.current = false;
        return () => {
            if (attemptInFlight.current) {
                attemptInFlight.current = false;
                baileysBridgeService
                    .cancelLink(organizationId, subOrganizationId, address)
                    // Same fallback as stopLinkAttempt, for a server without /cancel-link.
                    // Only reached for an attempt in flight, never a connected channel.
                    .catch(() => baileysBridgeService.disconnect(organizationId, subOrganizationId, address))
                    .catch(() => { /* leaving the page; nothing to report to */ });
            }
        };
    }, [organizationId, subOrganizationId, address]);

    useEffect(() => {
        // Only ever once per mount, and never against a link that is mid-handshake: a user
        // who has just scanned is seconds from connected, and cancelling there would throw
        // away a successful pairing and read on the phone as a failed link.
        if (adoptedExisting.current) return;
        // finalizingLink is showFinalizing's only variable here: status is already 'pending'
        // in the branch below, which is one of the two states showFinalizing covers.
        if (status === 'loading' || finalizingLink) return;
        adoptedExisting.current = true;
        if (status === 'pending' && !qrRequested) {
            stopLinkAttempt(false).then(() => setStatus('disconnected'));
        }
    }, [status, qrRequested, finalizingLink, stopLinkAttempt]);

    // Slow poll (10s) — detects external disconnect quickly
    useEffect(() => {
        const id = setInterval(fetchStatus, 10000);
        return () => clearInterval(id);
    }, [fetchStatus]);

    // Fast poll (3s) while reconnecting — either a user-driven relink or the backend
    // auto-reconnecting after a flap — so recovery (or exhaustion → reauth) shows quickly.
    useEffect(() => {
        if (!reconnecting && status !== 'reconnecting') return;
        const id = setInterval(fetchStatus, 3000);
        return () => clearInterval(id);
    }, [reconnecting, status, fetchStatus]);

    const handleDisconnect = async () => {
        setActionLoading(true);
        try {
            await baileysBridgeService.disconnect(organizationId, subOrganizationId, address);
            setStatus('disconnected');
            setPhone('');
            addNewNotifcation('WhatsApp disconnected.', 'success');
        } catch {
            addNewNotifcation('Failed to disconnect.', 'danger');
        } finally {
            setActionLoading(false);
        }
    };

    const handleQrReconnect = async () => {
        setActionLoading(true);
        setQrCode('');
        setQrRequested(true);
        endedPolls.current = 0;
        try {
            // forceReauth = the user explicitly clicked Re-authorize on a connected/reconnecting
            // card — tell the bridge to re-link even if the session is genuinely live. Without
            // it (panel shown for disconnected/reauth_required), no force is needed.
            // mode MUST be sent for a listener. The Bridge defaults an unqualified /qr to
            // "webhook" and PERSISTS it (whatsapp.service.ts initialize()), so linking a
            // listener from this page without it silently demoted the channel: it stopped
            // capturing own-side messages and started posting to the webhook path instead
            // of the listener path — i.e. the harvest quietly stopped.
            const res = await baileysBridgeService.generateQr(organizationId, subOrganizationId, address, isListener ? 'listener' : undefined, forceReauth);
            const q = res.data?.data?.qr as string | undefined;
            if (q) setQrCode(q);
            setStatus('pending');
            setReconnecting(true);
        } catch {
            addNewNotifcation('Failed to start QR session.', 'danger');
        } finally {
            setActionLoading(false);
        }
    };

    const handlePhoneReconnect = async () => {
        if (!pairingPhone.trim()) return;
        setActionLoading(true);
        try {
            const cleanPhone = pairingPhone.replace(/[^0-9]/g, '');
            const res = await baileysBridgeService.getPairingCode(organizationId, subOrganizationId, address, cleanPhone);
            const code = res.data?.data?.code as string | undefined;
            if (code) {
                setPairingCode(code);
                setStatus('pairing');
                setReconnecting(true);
            }
        } catch {
            addNewNotifcation('Failed to get pairing code.', 'danger');
        } finally {
            setActionLoading(false);
        }
    };

    // Reveal the QR/phone panel from a connected or reconnecting card so the user can force a
    // fresh link (e.g. when a session is expired or flapping and won't recover on its own).
    // Confirmed first: forcing a re-link wipes the bridge's stored credentials, which shows
    // as "logged out" in the phone's Linked Devices — an accidental click must not do that.
    const startReauth = () => {
        if (!window.confirm(
            'Re-authorizing will log this WhatsApp session out on the phone and require linking again. ' +
            'A session that is merely reconnecting usually recovers on its own. Continue?'
        )) return;
        setForceReauth(true);
        setReconnectMode('qr');
        setQrCode('');
        setPairingCode('');
    };

    // The bridge is inside WhatsApp's post-scan handshake (515 restart, ~2–10s). It can
    // report this window as either `pending` or `reconnecting` depending on which side of
    // the restart the poll lands on — render BOTH as the calm finalizing state, never as
    // "Awaiting Scan" (the user already scanned) or the amber unstable warning.
    const showFinalizing = finalizingLink && (status === 'reconnecting' || status === 'pending');

    // Backend flap/expiry states surface the relink panel just like a plain disconnect.
    const needsReauth = status === 'disconnected' || status === 'reauth_required';
    const showReconnectPanel = forceReauth || needsReauth || (status === 'pending' && !showFinalizing);

    if (status === 'loading') {
        return null;
    }

    return (
        <Card className={cn(
            'border',
            status === 'connected'
                ? 'border-emerald-200 dark:border-emerald-800'
                : needsReauth
                    ? 'border-red-200 dark:border-red-800'
                    : 'border-amber-200 dark:border-amber-800'
        )}>
            <CardHeader className="pb-3">
                <CardTitle className="text-sm flex items-center justify-between">
                    <div className="flex items-center gap-2">
                        <FaWhatsapp size={14} className="text-[#25D366]" />
                        WhatsApp Connection
                    </div>
                    {!reachable && (
                        <div
                            className="flex items-center gap-1.5 px-2 py-0.5 bg-slate-100 dark:bg-slate-800 rounded-full"
                            title="The bridge did not respond to the last status check. Showing the last known state."
                        >
                            <WifiOff size={10} className="text-slate-500" />
                            <span className="text-[10px] font-bold text-slate-600 dark:text-slate-300">Can’t reach bridge</span>
                        </div>
                    )}
                    {status === 'connected' && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950 rounded-full">
                            <Wifi size={10} className="text-emerald-600 dark:text-emerald-400" />
                            <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300">Connected</span>
                        </div>
                    )}
                    {(status === 'disconnected') && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-red-100 dark:bg-red-950 rounded-full">
                            <WifiOff size={10} className="text-red-600 dark:text-red-400" />
                            <span className="text-[10px] font-bold text-red-700 dark:text-red-300">Disconnected</span>
                        </div>
                    )}
                    {status === 'reauth_required' && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-red-100 dark:bg-red-950 rounded-full">
                            <WifiOff size={10} className="text-red-600 dark:text-red-400" />
                            <span className="text-[10px] font-bold text-red-700 dark:text-red-300">Session Expired</span>
                        </div>
                    )}
                    {showFinalizing && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-emerald-100 dark:bg-emerald-950 rounded-full">
                            <RefreshCw size={10} className="text-emerald-600 dark:text-emerald-400 animate-spin" />
                            <span className="text-[10px] font-bold text-emerald-700 dark:text-emerald-300">Finalizing Link…</span>
                        </div>
                    )}
                    {status === 'reconnecting' && !showFinalizing && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 dark:bg-amber-950 rounded-full">
                            <RefreshCw size={10} className="text-amber-600 dark:text-amber-400 animate-spin" />
                            <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">Reconnecting…</span>
                        </div>
                    )}
                    {status === 'degraded' && (
                        <div
                            className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 dark:bg-amber-950 rounded-full"
                            title="WhatsApp is refusing the connection for now. The credentials are still valid and the bridge keeps retrying — no action needed."
                        >
                            <RefreshCw size={10} className="text-amber-600 dark:text-amber-400" />
                            <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">Retrying…</span>
                        </div>
                    )}
                    {(status === 'pending' || status === 'pairing') && !showFinalizing && (
                        <div className="flex items-center gap-1.5 px-2 py-0.5 bg-amber-100 dark:bg-amber-950 rounded-full">
                            <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse" />
                            <span className="text-[10px] font-bold text-amber-700 dark:text-amber-300">
                                {status === 'pairing' ? 'Awaiting Code' : 'Awaiting Scan'}
                            </span>
                        </div>
                    )}
                </CardTitle>
            </CardHeader>

            <CardContent className="space-y-4">
                {/* Connected / reconnecting / finalizing state — phone + actions. A finalizing
                    session mid-handshake may report `pending`; it still gets this card (not the
                    QR panel — the user already scanned). */}
                {(status === 'connected' || status === 'reconnecting' || showFinalizing) && (
                    <div className="space-y-2">
                        <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                                <Phone size={13} className="text-muted-foreground" />
                                <span className="text-sm font-mono font-medium text-foreground">{phone || 'Unknown'}</span>
                                {status === 'connected' && linkedSince != null && (
                                    <span className="text-[11px] text-muted-foreground">
                                        · Linked since {new Date(linkedSince).toLocaleString('en-GB', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}
                                    </span>
                                )}
                            </div>
                            <div className="flex items-center gap-2">
                                {/* Hidden mid-handshake: re-authorizing here wipes creds that are
                                    seconds from opening and logs the device out on the phone. */}
                                {!finalizingLink && (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={startReauth}
                                    disabled={actionLoading}
                                    className="h-7 px-2.5 text-[11px] gap-1"
                                >
                                    <RefreshCw size={11} />
                                    Re-authorize
                                </Button>
                                )}
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleDisconnect}
                                    disabled={actionLoading}
                                    className="h-7 px-2.5 text-[11px] text-destructive border-destructive/40 hover:bg-destructive/5 gap-1"
                                >
                                    <PhoneOff size={11} />
                                    Disconnect
                                </Button>
                            </div>
                        </div>
                        {showFinalizing && (
                            <p className="text-[11px] text-muted-foreground">
                                Finalizing link — completing the WhatsApp handshake. This takes a few seconds.
                            </p>
                        )}
                        {status === 'reconnecting' && !showFinalizing && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                Connection unstable — attempting to reconnect. If it doesn’t recover, re-authorize.
                            </p>
                        )}
                        {status === 'connected' && staleMs != null && staleMs > 60000 && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                Last confirmed alive {Math.round(staleMs / 1000)}s ago — verifying the link is still live.
                            </p>
                        )}
                    </div>
                )}

                {/* Degraded — WhatsApp's edge is refusing a session whose credentials are
                    known good. Deliberately NO relink panel and NO QR button: the bridge
                    reports this state INSTEAD of reauth_required precisely so the UI does not
                    fire /qr, which would wipe valid credentials and register a new companion
                    device — manufacturing the orphan it was trying to fix. Show what is
                    happening and let the retry run. */}
                {status === 'degraded' && (
                    <div className="space-y-2">
                        {phone && (
                            <div className="flex items-center justify-between">
                                <span className="text-xs text-muted-foreground">Linked number</span>
                                <span className="text-xs font-mono text-foreground">{phone}</span>
                            </div>
                        )}
                        <p className="text-[11px] text-amber-600 dark:text-amber-400">
                            WhatsApp is refusing this connection for now. The credentials are still
                            valid and are being kept — the bridge retries on a slow cadence and the
                            channel recovers on its own.
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                            No action needed. Re-authorizing would replace working credentials with a
                            new device link, so it is intentionally not offered here.
                        </p>
                    </div>
                )}

                {/* Reconnect / re-authorize panel — QR + phone */}
                {showReconnectPanel && (
                    <div className="space-y-4 flex flex-col items-center text-center">
                        <p className="text-xs text-muted-foreground">
                            {status === 'pending'
                                ? 'Waiting for QR scan…'
                                : status === 'reauth_required'
                                    ? 'This session expired and needs to be re-authorized. Choose a method to reconnect.'
                                    : 'Choose a method to (re)connect this channel.'}
                        </p>
                        {forceReauth && status === 'connected' && (
                            <p className="text-[11px] text-amber-600 dark:text-amber-400">
                                This channel is currently connected. Getting a new QR will disconnect the
                                current session — scan the new code to re-link.
                            </p>
                        )}

                        {/* Mode toggle */}
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant={reconnectMode === 'qr' ? 'default' : 'outline'}
                                className="h-7 px-3 text-[11px]"
                                onClick={() => setReconnectMode('qr')}
                            >
                                QR Code
                            </Button>
                            <Button
                                size="sm"
                                variant={reconnectMode === 'phone' ? 'default' : 'outline'}
                                className="h-7 px-3 text-[11px]"
                                onClick={() => setReconnectMode('phone')}
                            >
                                Phone Number
                            </Button>
                        </div>

                        {reconnectMode === 'qr' && (
                            (qrRequested || qrCode) ? (
                                // Reuse the create-channel connection panel verbatim: QR card,
                                // countdown, progress bar, and step checklist. 'pending' with an
                                // empty qrCode is its built-in Generating state.
                                <div className="flex flex-col items-center text-center gap-4 w-full">
                                    <BaileysConnectionPanel connectionStatus="pending" qrCode={qrCode} />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleStop}
                                        disabled={actionLoading}
                                        className="h-7 px-2.5 text-[11px] gap-1"
                                    >
                                        <X size={11} />
                                        Stop
                                    </Button>
                                </div>
                            ) : (
                                <Button
                                    size="sm"
                                    variant="outline"
                                    onClick={handleQrReconnect}
                                    disabled={actionLoading}
                                    className="text-xs gap-1.5"
                                >
                                    <RefreshCw size={12} className={cn(actionLoading && 'animate-spin')} />
                                    {actionLoading ? 'Starting…' : 'Get QR Code'}
                                </Button>
                            )
                        )}

                        {reconnectMode === 'phone' && (
                            pairingCode ? (
                                <div className="flex flex-col items-center text-center gap-4 w-full">
                                    <BaileysConnectionPanel connectionStatus="pairing" qrCode="" pairingCode={pairingCode} />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handleStop}
                                        disabled={actionLoading}
                                        className="h-7 px-2.5 text-[11px] gap-1"
                                    >
                                        <X size={11} />
                                        Stop
                                    </Button>
                                </div>
                            ) : (
                                <div className="flex flex-col gap-2 w-full max-w-xs">
                                    <PhoneInput
                                        country={'my'}
                                        value={pairingPhone}
                                        onChange={setPairingPhone}
                                        inputClass="!w-full !h-9 !pl-11 !text-xs !font-normal"
                                        containerClass="!w-full"
                                        buttonClass="!border-border"
                                        specialLabel=""
                                    />
                                    <Button
                                        size="sm"
                                        variant="outline"
                                        onClick={handlePhoneReconnect}
                                        disabled={actionLoading || !pairingPhone.trim()}
                                        className="text-xs gap-1.5"
                                    >
                                        <RefreshCw size={12} className={cn(actionLoading && 'animate-spin')} />
                                        {actionLoading ? 'Getting code…' : 'Get Pairing Code'}
                                    </Button>
                                </div>
                            )
                        )}
                    </div>
                )}

            </CardContent>
        </Card>
    );
};
